import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Building2, Award, Hash, BadgeCheck, MapPin, CheckCircle2, Users2, Quote } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/PageHeader";

const Profil = () => {
  const { settings } = useSite();
  const [p, setP] = useState({});
  useSeo("Profil Sekolah", "Sejarah, identitas, struktur, dan fasilitas Madrasah Ibtidaiyah Futuhiyyah Doro, Pekalongan.");

  useEffect(() => { api.get("/profile").then((r) => setP(r.data)).catch(() => {}); }, []);

  const identity = [
    { icon: BadgeCheck, label: "Status", value: p.status_sekolah },
    { icon: Award, label: "Akreditasi", value: p.accreditation },
    { icon: Hash, label: "NPSN", value: p.npsn },
    { icon: MapPin, label: "Alamat", value: p.address },
  ];

  return (
    <div>
      <PageHeader title="Profil Madrasah" subtitle="Mengenal lebih dekat sejarah, identitas, dan fasilitas MI Futuhiyyah Doro." crumbs={[{ label: "Profil" }]} />
      <div className="mx-auto max-w-5xl px-6 py-14 space-y-16">
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-3">
            <Building2 className="h-6 w-6 text-emerald-600" />
            <h2 className="font-heading text-2xl font-bold text-slate-900">Sejarah Singkat</h2>
          </div>
          <p className="mt-4 text-base leading-relaxed text-slate-600">{p.history}</p>
        </motion.section>

        <section>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Identitas Sekolah</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {identity.map((it) => (
              <div key={it.label} className="flex items-start gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"><it.icon className="h-5 w-5" /></div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{it.label}</p>
                  <p className="mt-1 font-medium text-slate-800">{it.value || "-"}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="grid items-center gap-8 rounded-3xl bg-emerald-50/70 p-8 md:grid-cols-3">
          <div className="mx-auto max-w-[220px]">
            <img src={resolveImg(settings.headmaster_photo_url)} alt={settings.headmaster_name} className="rounded-t-[2rem] rounded-b-xl object-cover aspect-[3/4] w-full shadow" />
            <p className="mt-3 text-center font-heading font-bold text-slate-900">{settings.headmaster_name}</p>
            <p className="text-center text-sm text-emerald-700">Kepala Madrasah</p>
          </div>
          <div className="md:col-span-2">
            <Quote className="h-8 w-8 text-emerald-300" />
            <p className="mt-2 text-lg leading-relaxed text-slate-700">{settings.headmaster_message}</p>
          </div>
        </section>

        <section>
          <div className="flex items-center gap-3">
            <Users2 className="h-6 w-6 text-emerald-600" />
            <h2 className="font-heading text-2xl font-bold text-slate-900">Struktur Organisasi</h2>
          </div>
          <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200/80">
            {(p.structure || []).map((s, i) => (
              <div key={i} className={`flex items-center justify-between px-5 py-4 ${i % 2 ? "bg-white" : "bg-slate-50/70"}`}>
                <span className="text-sm text-slate-500">{s.role}</span>
                <span className="font-medium text-slate-800">{s.name}</span>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Fasilitas Sekolah</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {(p.facilities || []).map((f, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                <span className="text-sm text-slate-700">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-2xl bg-slate-50 p-6">
          <h2 className="font-heading text-xl font-bold text-slate-900">Informasi Umum</h2>
          <p className="mt-3 text-slate-600 leading-relaxed">{p.general_info}</p>
        </section>
      </div>
    </div>
  );
};

export default Profil;
